import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { BillStatus } from '@enview/interface/types/Bill';

import BillTrackingButton from '../../Shared/BillTracking/BillTrackingButton';
import BillTagButton from '../../Shared/BillTagging/BillTagButton';
import { State } from '../../../dux/@types';
import { BillAPI } from '../../../api';
import { getBillTitle, translateBillStatusForBadge } from '../../../helpers/BillHelper';
import { Link } from 'react-router-dom';
import BillInfoPopoverSkeleton from '../../../components/skeletons/BillInfoPopoverSkeleton';

interface BillInfoPopoverProps {
  billId: string;
}

export const BillInfoPopover: React.FC<BillInfoPopoverProps> = ({ billId }) => {
  const teamId = useSelector((state: State) => state.teamMode.teamId);

  const { data: bill, isLoading } = BillAPI.endpoints.getBill.useQuery({
    billId,
    teamId,
  });

  const userAliases = bill?.userAliases ?? [];

  const alias = useMemo(() => {
    if (!userAliases.length || !teamId) return;
    return userAliases.find((a) => a.teamId === teamId)?.alias;
  }, [userAliases, teamId]);

  const status = useMemo(
    () => (bill?.status as BillStatus) ?? BillStatus.INTRODUCED,
    [bill?.status],
  );

  const billCommittees = useMemo(() => {
    if (!bill?.committeeMentions?.length) return '(no committee)';
    const committeeNames = bill.committeeMentions
      .filter((mention) => mention.name)
      .map((mention) => mention.name);
    return committeeNames.length ? committeeNames.join(', ') : '(no committee)';
  }, [bill?.committeeMentions]);

  if (isLoading || !bill) return <BillInfoPopoverSkeleton />;

  return (
    <div className="bill-info-popover d-flex flex-column">
      <div className="popover-header">
        <p className="bill-status">{translateBillStatusForBadge(status)}</p>
        <div className="popover-actions d-flex">
          <BillTrackingButton bill={bill} hasStatusIndicator />
          <BillTagButton
            bill={bill}
            hasStatusIndicator
            placeHolderText="tag"
            showPlaceholderText
          />
        </div>
      </div>
      <Link className="bill-title" to={`/legislative-tracking/bill/details/${bill.id}`}>
        {bill.session.state.name + getBillTitle(bill.session, bill.number)}
      </Link>
      {alias && <div className="bill-shortname">{alias}</div>}
      <div className="bill-summary">{bill.name}</div>
      <div className="popover-committees">{billCommittees}</div>
    </div>
  );
};

export default BillInfoPopover;
